import { readCogoDocumentWorkspaceFromProject } from '@/features/document-workspace/model/document-operation'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useStudioStore } from '@/store/studio-store'
import type { StudioCapabilityPlan } from './studio-capability-gateway'

export interface StudioSemanticSyncInput {
  changeLabel: string
  pageId: string
  appTitle: string
  primaryColor: string
  documentNodeId: string
  guideText: string
}

function requiredText(value: unknown, label: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`${label} is required for an exact semantic sync.`)
  }
  return value.trim()
}

export function parseStudioSemanticSyncInput(
  params: Record<string, unknown>,
): StudioSemanticSyncInput {
  const input: StudioSemanticSyncInput = {
    changeLabel: requiredText(params.changeLabel, 'changeLabel'),
    pageId: requiredText(params.pageId, 'pageId'),
    appTitle: requiredText(params.appTitle, 'appTitle'),
    primaryColor: requiredText(params.primaryColor, 'primaryColor'),
    documentNodeId: requiredText(params.documentNodeId, 'documentNodeId'),
    guideText: requiredText(params.guideText, 'guideText'),
  }
  if (!/^#[0-9a-f]{6}$/i.test(input.primaryColor)) {
    throw new Error('primaryColor must be an exact six-digit hex color.')
  }
  const pageMatches = (useEditorStore.getState().app?.pages ?? []).filter(
    (page) => page.id === input.pageId,
  )
  if (pageMatches.length !== 1) {
    throw new Error(
      `Semantic sync expected one application page ${input.pageId}, found ${pageMatches.length}.`,
    )
  }
  const project = useStudioStore.getState().currentProject
  const workspace = readCogoDocumentWorkspaceFromProject(project)
  if (!workspace?.graph.nodes[input.documentNodeId]) {
    throw new Error(
      `Semantic sync document node ${input.documentNodeId} is unavailable in the canonical Guide workspace.`,
    )
  }
  return input
}

export function buildStudioSemanticSyncPlan(input: {
  params: Record<string, unknown>
  planId: string
  runId?: string
  parentRunId?: string
}): StudioCapabilityPlan {
  const change = parseStudioSemanticSyncInput(input.params)
  const pageTitlePath = `/manifest/pages/${encodeURIComponent(change.pageId)}/title`
  const primaryColorPath = '/tokens/colors/primary'
  const guideTextPath = `/cogoDocumentWorkspace/nodes/${encodeURIComponent(change.documentNodeId)}/text`
  const exactReadbackTool = 'cogo.studio.capability.readbackExactImpact'
  return {
    planId: input.planId,
    ...(input.runId ? { runId: input.runId } : {}),
    ...(input.parentRunId ? { parentRunId: input.parentRunId } : {}),
    label: `Sync ${change.changeLabel} across App, Design, Guide, and release`,
    intent:
      'Apply one bounded semantic change set, prove each exact target, then prepare one immutable publication candidate.',
    source: 'chat-operation',
    commands: [
      {
        operationId: 'page.details.rename',
        tool: 'cogo.studio.updatePageMeta',
        params: { pageId: change.pageId, title: change.appTitle },
        requiresConfirmation: true,
        readback: {
          tool: exactReadbackTool,
          params: {
            checks: [{ path: pageTitlePath, expected: change.appTitle }],
          },
        },
      },
      {
        operationId: 'theme.color.update',
        tool: 'cogo.studio.updateThemeColor',
        params: { key: 'primary', value: change.primaryColor },
        requiresConfirmation: true,
        readback: {
          tool: exactReadbackTool,
          params: {
            checks: [{ path: primaryColorPath, expected: change.primaryColor }],
          },
        },
      },
      {
        operationId: 'document.page.update_node',
        tool: 'cogo.studio.document.update_node',
        params: { nodeId: change.documentNodeId, text: change.guideText },
        requiresConfirmation: true,
        readback: {
          tool: exactReadbackTool,
          params: {
            checks: [{ path: guideTextPath, expected: change.guideText }],
          },
        },
      },
      {
        operationId: 'project.publish.confirm',
        tool: 'cogo.studio.publishProject',
        params: {},
        requiresConfirmation: true,
      },
    ],
  }
}
